import { TimeZone } from "client/DateAndTime/timezones";
import { GlobalSearchItemPersistedModel } from "../../Persistence/models";
import {
	ReactElement,
	useCallback,
	useEffect
} from "react";
import { isValid } from "../../Persistence/validation";
import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { AnnouncementItemView } from "./AnnouncementItemView";
import { AppItemView } from "./AppItemView";
import { CommunityItemView } from "./CommunityItemView";
import { CommunityPostItemView } from "./CommunityPostItemView";
import { ContactItemView } from "./ContactItemView";
import { ContactSource } from "client/Api/Email/contacts";
import { DocumentItemView } from "./DocumentItemView";
import { EgnyteItemView } from "./EgnyteItemView";
import { EmailItemView } from "./EmailItemView";
import { EventItemView } from "./EventItemView";
import { IntegrationFlowItemView } from "./IntegrationFlowItemView";
import { KnowledgeItemView } from "./KnowledgeItemView";
import { logNever } from "client_legacy/ndaw/errors";

export type ItemViewProps = {
	item: GlobalSearchItemPersistedModel;
	navigationIndex: number;
	index: number;
	isMobile: boolean;
	onRemove: (item: GlobalSearchItemPersistedModel) => void;
	onOpenContactDetails: (id: string, contactSource: ContactSource) => void;
	timezone: TimeZone;
};

export const ItemView = (props: ItemViewProps): ReactElement | null => {
	const {
		item,
		navigationIndex,
		index,
		isMobile,
		onRemove,
		onOpenContactDetails,
		timezone
	} = props;

	const isItemValid: boolean = isValid(item);

	useEffect(
		(): void => {
			if (!isItemValid) {
				console.error(`No valid data is set for the recently opened item with kind '${item.kind}'.`);
			}
		},
		[
			isItemValid,
			item.kind
		]
	);

	const removeHandler = useCallback(
		(): void => {
			onRemove(item);
		},
		[item, onRemove]
	);

	if (!isValid(item)) {
		return null;
	}

	switch (item.kind) {
		case GlobalSearchResultItemKind.Announcement:
			return (
				<AnnouncementItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.App:
			return (
				<AppItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Community:
			return (
				<CommunityItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
				/>
			);
		case GlobalSearchResultItemKind.CommunityPost:
			return (
				<CommunityPostItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Contact:
			return (
				<ContactItemView
					item={item}
					navigationIndex={navigationIndex}
					onOpenContactDetails={onOpenContactDetails}
					onRemove={removeHandler}
				/>
			);
		case GlobalSearchResultItemKind.Document:
			return (
				<DocumentItemView
					item={item}
					navigationIndex={navigationIndex}
					index={index}
					isMobile={isMobile}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.EgnyteItem:
			return (
				<EgnyteItemView
					item={item}
					navigationIndex={navigationIndex}
					isMobile={isMobile}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Email:
			return (
				<EmailItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.Event:
			return (
				<EventItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.IntegrationFlow:
			return (
				<IntegrationFlowItemView
					item={item}
					navigationIndex={navigationIndex}
					index={index}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		case GlobalSearchResultItemKind.KnowledgeItems:
			return (
				<KnowledgeItemView
					item={item}
					navigationIndex={navigationIndex}
					onRemove={removeHandler}
					timezone={timezone}
				/>
			);
		default:
			logNever(item);
			return null;
	}
};
ItemView.displayName = "ItemView";